/**
 * Browser-wallet connection — Keplr and Leap.
 *
 * Deliberately free of React and of any network call beyond the injected
 * wallet API, because this is the one part of the tenant surface that cannot be
 * verified against the real thing here: it needs a browser with the extension
 * installed. Keeping it a pure module means the *logic* — chain suggestion,
 * enable, address extraction, and every failure path — is testable against a
 * mock `window.keplr`, and what remains unverified shrinks to "does the real
 * extension behave as its documentation says".
 *
 * No CosmJS. Phase A only needs an address: the console's own /api/tenant/*
 * endpoints do the chain reads. Signing arrives in Phase B and brings
 * @cosmjs/* with it — pulling that dependency in now would be paying for it
 * before it does anything.
 */

export type WalletKind = 'keplr' | 'leap'

export interface ChainConfig {
  chain_id: string
  denom: string
  rest: string
  rpc: string
}

export interface Connection {
  wallet: WalletKind
  address: string
  name: string
  publicKey?: Uint8Array
}

/** A wallet failure the UI is expected to explain rather than swallow. */
export class WalletError extends Error {
  readonly kind:
    | 'not-installed'
    | 'locked'
    | 'rejected'
    | 'unknown-chain'
    | 'no-chain-id'
    | 'timeout'
    | 'cancelled'
    | 'failed'

  /** Which extension produced this, so a recovery surface can name it. */
  readonly wallet: WalletKind | undefined

  constructor(kind: WalletError['kind'], message: string, wallet?: WalletKind) {
    super(message)
    this.name = 'WalletError'
    this.kind = kind
    this.wallet = wallet
  }
}

/** The user-facing name of an extension. One spelling, used everywhere. */
export const WALLET_LABEL: Record<WalletKind, string> = { keplr: 'Keplr', leap: 'Leap' }

/**
 * How long one extension call may go unanswered before the console gives up.
 *
 * A dismissed prompt does not always settle the extension's promise: closing
 * or skipping a Keplr/Leap window can leave the call pending, and nothing the
 * page does can make it answer. Awaited without a bound, that pending call
 * held the console's one-request-at-a-time guard forever and left the wallet
 * sheet on "Waiting for Keplr" with no action to take. Generous, because a
 * person may be unlocking the wallet or reading what they are asked to sign.
 */
export const WALLET_REQUEST_TIMEOUT_MS = 120_000

/** Lets the caller stop waiting for the extension, and bounds the wait. */
export interface WalletRequestOptions {
  signal?: AbortSignal | undefined
  timeoutMs?: number | undefined
}

/**
 * Await one extension call, but never longer than the bound or past an abort.
 *
 * The extension's own promise is left behind rather than cancelled — no wallet
 * API offers that — so the extension may still show the old prompt, and the
 * copy for both outcomes tells the user to clear it there.
 */
export function answered<T>(
  call: Promise<T>,
  kind: WalletKind,
  { signal, timeoutMs = WALLET_REQUEST_TIMEOUT_MS }: WalletRequestOptions = {},
): Promise<T> {
  const label = WALLET_LABEL[kind]
  const cancelled = () => new WalletError('cancelled', `Stopped waiting for ${label}.`, kind)
  if (signal?.aborted) return Promise.reject(cancelled())
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      cleanup()
      reject(new WalletError(
        'timeout',
        `${label} did not answer within ${Math.round(timeoutMs / 60_000) || 1} minutes.`,
        kind,
      ))
    }, timeoutMs)
    const onAbort = () => {
      cleanup()
      reject(cancelled())
    }
    const cleanup = () => {
      clearTimeout(timer)
      signal?.removeEventListener('abort', onAbort)
    }
    signal?.addEventListener('abort', onAbort, { once: true })
    call.then(
      (value) => { cleanup(); resolve(value) },
      (error: unknown) => { cleanup(); reject(error) },
    )
  })
}

/** An outcome of `answered` itself, which no caller may reclassify. */
function isUnanswered(err: unknown): err is WalletError {
  return err instanceof WalletError && (err.kind === 'timeout' || err.kind === 'cancelled')
}

/**
 * The subset of the injected wallet API this module uses.
 *
 * Typed structurally rather than imported from @keplr-wallet/types so the build
 * does not depend on a package solely for four method signatures.
 */
interface InjectedWallet {
  enable(chainId: string): Promise<void>
  getKey(chainId: string): Promise<{ bech32Address: string; name?: string; pubKey?: Uint8Array }>
  experimentalSuggestChain?(config: unknown): Promise<void>
  signArbitrary?(chainId: string, signer: string, data: string): Promise<WalletSignature>
  signAmino?(chainId: string, signer: string, doc: AminoSignDoc): Promise<WalletSignature>
}

interface WalletSignature {
  signature?: string | { signature?: string }
  pub_key?: { type?: string; value?: string }
}

export interface AminoSignDoc {
  chain_id: string
  account_number: string
  sequence: string
  fee: { amount: []; gas: string }
  msgs: [{ type: 'sign/MsgSignData'; value: { signer: string; data: string } }]
  memo: string
}

/**
 * The wallet has been switched to an account other than the session's.
 *
 * A remembered wallet is restored from the session without asking the
 * extension, so the console's address and the wallet's active account can
 * drift apart; every signing path names that rather than failing on it.
 */
export function accountMismatch(active: string | undefined, expected: string, kind?: WalletKind): WalletError {
  return new WalletError(
    'failed',
    `Your wallet's active account (${active ?? 'none'}) is not the one you are signed in as (${expected}). Switch accounts in the wallet and try again.`,
    kind,
  )
}

/** Ask the connected extension to sign an ADR-36 message. The result is opaque
 * wallet output; callers may place it in a JWT but cannot mint it themselves. */
export async function signArbitrary(
  kind: WalletKind,
  config: ChainConfig,
  address: string,
  data: string,
  win: WalletWindow = globalThis as WalletWindow,
  options: WalletRequestOptions = {},
): Promise<string> {
  const chainId = config.chain_id
  await ensureChain(kind, config, win, options)
  const wallet = pick(kind, win)
  if (!wallet.signArbitrary && !wallet.signAmino) throw new WalletError('failed', `${kind} does not support arbitrary signing.`)
  // A key the wallet will not report is left for the signature to fail on,
  // with the wallet's own words; only a positive mismatch is named here.
  const active = await answered(wallet.getKey(chainId), kind, options).then(
    (key) => key?.bech32Address,
    (error: unknown) => {
      if (isUnanswered(error)) throw error
      return undefined
    },
  )
  if (active && active !== address) throw accountMismatch(active, address, kind)
  try {
    if (wallet.signArbitrary) {
      const result = await answered(wallet.signArbitrary(chainId, address, data), kind, options)
      const signature = typeof result?.signature === 'string' ? result.signature : result?.signature?.signature
      if (!signature) throw new Error('The wallet returned no signature.')
      return signature
    }
    const result = await answered(wallet.signAmino!(chainId, address, adr36SignDoc(chainId, address, data)), kind, options)
    const signature = typeof result?.signature === 'string' ? result.signature : result?.signature?.signature
    if (!signature) throw new Error('The wallet returned no signature.')
    return signature
  } catch (error) {
    if (isUnanswered(error)) throw error
    if (isRejection(error)) throw new WalletError('rejected', 'You declined the provider access request.', kind)
    throw new WalletError('failed', `The wallet could not sign provider access: ${error instanceof Error ? error.message : String(error)}`, kind)
  }
}

/** Sign a console login challenge and return the public key alongside it. */
export async function signCosmosLogin(
  kind: WalletKind,
  chainId: string,
  address: string,
  data: string,
  publicKey: Uint8Array | undefined,
  win: WalletWindow = globalThis as WalletWindow,
  options: WalletRequestOptions = {},
): Promise<{ signature: string; pubKey: string; pubKeyType: string }> {
  const wallet = pick(kind, win)
  if (!wallet.signArbitrary && !wallet.signAmino) throw new WalletError('failed', `${kind} does not support arbitrary signing.`)
  try {
    const result = await answered(
      wallet.signArbitrary
        ? wallet.signArbitrary(chainId, address, data)
        : wallet.signAmino!(chainId, address, adr36SignDoc('', address, data)),
      kind,
      options,
    )
    const signature = typeof result?.signature === 'string' ? result.signature : result?.signature?.signature
    const pubKey = result?.pub_key?.value ?? encodeBase64(publicKey)
    if (!signature) throw new Error('The wallet returned no signature.')
    if (!pubKey) throw new Error('The wallet returned no public key.')
    return {
      signature,
      pubKey,
      pubKeyType: result?.pub_key?.type ?? 'tendermint/PubKeySecp256k1',
    }
  } catch (error) {
    if (isUnanswered(error)) throw error
    if (isRejection(error)) throw new WalletError('rejected', 'You declined the DDWS sign-in request.', kind)
    throw new WalletError('failed', `The wallet could not sign DDWS sign-in: ${error instanceof Error ? error.message : String(error)}`, kind)
  }
}

function adr36SignDoc(chainId: string, address: string, data: string): AminoSignDoc {
  return {
    chain_id: chainId,
    account_number: '0',
    sequence: '0',
    fee: { amount: [], gas: '0' },
    msgs: [{ type: 'sign/MsgSignData', value: { signer: address, data: utf8Base64(data) } }],
    memo: '',
  }
}

function utf8Base64(value: string): string {
  const bytes = new TextEncoder().encode(value)
  let binary = ''
  bytes.forEach((byte) => { binary += String.fromCharCode(byte) })
  return btoa(binary)
}

type WalletWindow = typeof globalThis & {
  keplr?: InjectedWallet
  leap?: InjectedWallet
}

export function available(win: WalletWindow = globalThis as WalletWindow): WalletKind[] {
  const found: WalletKind[] = []
  if (win.keplr) found.push('keplr')
  if (win.leap) found.push('leap')
  return found
}

/**
 * Watch for an extension that has not injected itself yet.
 *
 * Neither Keplr nor Leap fires an "I am here" event, so polling on a bounded
 * schedule is the honest mechanism: it either finds the extension or stops.
 * Bounded rather than indefinite because a timer that never ends is a battery
 * cost paid by every visitor who simply has no wallet. `onChange` fires only
 * when the set actually changes, so a caller can drive React state with it.
 *
 * Framework-free on purpose — the same function backs the React hook and the
 * tests, and the tests need no extension and no browser.
 */
export function watchWallets(
  onChange: (wallets: WalletKind[]) => void,
  win: WalletWindow = globalThis as WalletWindow,
  { intervalMs = 300, timeoutMs = 3000 }: { intervalMs?: number; timeoutMs?: number } = {},
): () => void {
  let previous = available(win).join()
  let elapsed = 0
  const timer = setInterval(() => {
    elapsed += intervalMs
    const next = available(win)
    if (next.join() !== previous) {
      previous = next.join()
      onChange(next)
    }
    if (elapsed >= timeoutMs) clearInterval(timer)
  }, intervalMs)
  return () => clearInterval(timer)
}

function pick(kind: WalletKind, win: WalletWindow): InjectedWallet {
  const wallet = kind === 'keplr' ? win.keplr : win.leap
  if (!wallet) {
    // No "reload" instruction: re-detection is an in-page action now.
    throw new WalletError('not-installed', `${WALLET_LABEL[kind]} was not detected.`, kind)
  }
  return wallet
}

/**
 * A wallet that is locked, and a wallet that is waiting for the user to approve
 * something, are the same observable: neither Keplr nor Leap exposes a keyring
 * state we may rely on, and a locked wallet's `enable` simply stays pending
 * behind its own unlock prompt. So this matches only the messages that say so
 * outright, and the copy it drives has to cover both readings. It is checked
 * before `isRejection` because dismissing an unlock prompt reports a rejection.
 */
function isLocked(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err)
  // \b before 'lock' so 'blocked' — an unrelated network failure — does not match.
  return /\b(?:un)?lock|key ?ring/i.test(message)
}

/**
 * Build the chain descriptor a wallet needs to add a chain it has never seen.
 *
 * A private DDWS chain is not in any wallet's registry, so `enable` alone fails
 * with an unknown-chain error and the only way through is to suggest it first.
 * Derived from /api/tenant/chain rather than hardcoded, so a redeployed cluster
 * with a new chain id does not need a front-end change.
 */
export function suggestion(config: ChainConfig) {
  const denom = config.denom || 'uddt'
  // Cosmos convention: the display denomination drops the 'u' (micro) prefix.
  const display = denom.startsWith('u') ? denom.slice(1).toUpperCase() : denom.toUpperCase()
  const currency = { coinDenom: display, coinMinimalDenom: denom, coinDecimals: 6 }
  return {
    chainId: config.chain_id,
    chainName: config.chain_id,
    rpc: config.rpc,
    rest: config.rest,
    bip44: { coinType: 118 },
    bech32Config: {
      bech32PrefixAccAddr: 'ddws',
      bech32PrefixAccPub: 'ddwspub',
      bech32PrefixValAddr: 'ddwsvaloper',
      bech32PrefixValPub: 'ddwsvaloperpub',
      bech32PrefixConsAddr: 'ddwsvalcons',
      bech32PrefixConsPub: 'ddwsvalconspub',
    },
    currencies: [currency],
    // The network's gas price is zero (ADR-003), so every step is zero: a
    // wallet that prices a fee itself prices it from these. The fee currency
    // stays, because a wallet needs one to build any fee at all, an empty one
    // included.
    feeCurrencies: [{ ...currency, gasPriceStep: { low: 0, average: 0, high: 0 } }],
    stakeCurrency: currency,
  }
}

function isRejection(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err)
  return /reject|denied|declin|cancel/i.test(message)
}

/**
 * Make sure the extension knows the chain and has approved this site for it.
 *
 * Suggestion is attempted before `enable` and its failure is not fatal: a wallet
 * that already knows the chain may not implement `experimentalSuggestChain`, and
 * treating that as an error would block the case that needs no suggestion at all.
 *
 * Run by `connect`, and again before every signature: the console restores a
 * remembered wallet from the session without asking the extension, so a page
 * may reach its first signature without having registered the chain — after a
 * re-genesis, a wallet reset, or the user removing the chain. Running it at
 * signing time means any prompt it raises answers a click.
 */
export async function ensureChain(
  kind: WalletKind,
  config: ChainConfig,
  win: WalletWindow = globalThis as WalletWindow,
  options: WalletRequestOptions = {},
): Promise<void> {
  if (!config.chain_id) {
    throw new WalletError(
      'no-chain-id',
      'This console is not configured for a chain yet. Its operator needs to set CHAIN_ID.',
      kind,
    )
  }
  const wallet = pick(kind, win)

  if (wallet.experimentalSuggestChain) {
    try {
      await answered(wallet.experimentalSuggestChain(suggestion(config)), kind, options)
    } catch (err) {
      // A rejected *suggestion* is the user declining to add the chain, which
      // enable() cannot recover from — so that one does propagate. So does a
      // suggestion nobody answered: enable() would only queue behind it.
      if (isUnanswered(err)) throw err
      if (isLocked(err)) {
        throw new WalletError('locked', `Open ${WALLET_LABEL[kind]} to continue — it may be locked or waiting for you.`, kind)
      }
      if (isRejection(err)) {
        throw new WalletError('rejected', 'You declined to add this chain to your wallet.', kind)
      }
    }
  }

  try {
    await answered(wallet.enable(config.chain_id), kind, options)
  } catch (err) {
    if (isUnanswered(err)) throw err
    if (isLocked(err)) {
      throw new WalletError('locked', `Open ${WALLET_LABEL[kind]} to continue — it may be locked or waiting for you.`, kind)
    }
    if (isRejection(err)) {
      throw new WalletError('rejected', 'You declined the connection request.', kind)
    }
    throw new WalletError(
      'unknown-chain',
      `The wallet could not open chain ${config.chain_id}: ${
        err instanceof Error ? err.message : String(err)
      }`,
      kind,
    )
  }
}

/** Connect, returning the account the user approved. */
export async function connect(
  kind: WalletKind,
  config: ChainConfig,
  win: WalletWindow = globalThis as WalletWindow,
  options: WalletRequestOptions = {},
): Promise<Connection> {
  await ensureChain(kind, config, win, options)
  const wallet = pick(kind, win)

  let key: { bech32Address: string; name?: string; pubKey?: Uint8Array }
  try {
    key = await answered(wallet.getKey(config.chain_id), kind, options)
  } catch (err) {
    if (isUnanswered(err)) throw err
    if (isLocked(err)) {
      throw new WalletError('locked', `Open ${WALLET_LABEL[kind]} to continue — it may be locked or waiting for you.`, kind)
    }
    throw new WalletError(
      'failed',
      `Connected, but the wallet did not return an account: ${
        err instanceof Error ? err.message : String(err)
      }`,
      kind,
    )
  }

  if (!key?.bech32Address) {
    throw new WalletError('failed', 'The wallet returned no address for this chain.', kind)
  }
  return key.pubKey
    ? { wallet: kind, address: key.bech32Address, name: key.name ?? '', publicKey: key.pubKey }
    : { wallet: kind, address: key.bech32Address, name: key.name ?? '' }
}

function encodeBase64(value: Uint8Array | undefined): string | undefined {
  if (!value) return undefined
  let binary = ''
  value.forEach((byte) => { binary += String.fromCharCode(byte) })
  return btoa(binary)
}
